import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import VideoSelectionModal from "./VideoSelectionModal.jsx";
import "./VideoReferenceEditor.css";
import ArrowLeftIcon from "../../assets/images/arrow-left.svg";
import VideoIcon from "../../assets/images/Video-Icon.svg";
import DeleteIcon from "../../assets/images/Delete-icon.svg";
import Header from "../../Components/Header/Header.js";

export default function VideoReferenceEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  const timeInputRef = useRef(null);
  const uploadedVideo = location.state?.uploadedVideo;
  const [showModal, setShowModal] = useState(false);
  const [selectedTimestamp, setSelectedTimestamp] = useState("");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [videoDuration, setVideoDuration] = useState(0);
  const [timestampError, setTimestampError] = useState("");
  const [videoError, setVideoError] = useState("");

  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return [
      String(hours).padStart(2, "0"),
      String(minutes).padStart(2, "0"),
      String(seconds).padStart(2, "0"),
    ].join(":");
  };

  const generateTimestampOptions = () => {
    const options = [];
    const step = videoDuration > 3600 ? 30 : 5;
    for (let i = 0; i <= videoDuration; i += step) {
      options.push(formatTime(i));
    }
    if (
      videoDuration > 0 &&
      options[options.length - 1] !== formatTime(videoDuration)
    ) {
      options.push(formatTime(videoDuration));
    }

    return options;
  };

  const getTimestampInSeconds = (value) => {
    const normalized = value
      .trim()
      .toLowerCase()
      .replace(/\b(hours?|hour)\b/g, "")
      .replace(/\b(minutes?|mins?|min)\b/g, "")
      .replace(/\b(seconds?|secs?|sec)\b/g, "")
      .trim();

    const parts = normalized.split(":").map(Number);

    if (parts.some(isNaN)) return null;

    let hours = 0;
    let minutes = 0;
    let seconds = 0;

    if (parts.length === 3) {
      [hours, minutes, seconds] = parts;
    } else if (parts.length === 2) {
      [minutes, seconds] = parts;
    } else if (parts.length === 1) {
      [seconds] = parts;
    } else {
      return null;
    }

    return hours * 3600 + minutes * 60 + seconds;
  };

  const isValidTimestamp = (value) => {
    return /^(\d{1,2}:)?(\d{1,2}:)?\d{1,2}\s+(hour|hours|min|mins|minute|minutes|sec|secs|second|seconds)$/i.test(
      value.trim(),
    );
  };
  const addVideoToList = () => {
    let hasError = false;

    setTimestampError("");
    setVideoError("");

    if (!selectedTimestamp) {
      setTimestampError("Please enter a timestamp");
      hasError = true;
    } else if (!isValidTimestamp(selectedTimestamp)) {
      setTimestampError(
        "Please enter a timestamp with a unit (e.g. 08:30 min, 01:20:15 hour, 45 sec)",
      );
      hasError = true;
    }
    const normalizeTimestamp = (value) => {
      return value
        .trim()
        .toLowerCase()
        .replace(/\bseconds?\b/g, "sec")
        .replace(/\bsecs\b/g, "sec")
        .replace(/\bminutes?\b/g, "min")
        .replace(/\bmins\b/g, "min")
        .replace(/\bhours?\b/g, "hour");
    };

    const duplicateTimestamp = tableData.some(
      (item) =>
        getTimestampInSeconds(item.timestamp) ===
        getTimestampInSeconds(selectedTimestamp),
    );

    if (duplicateTimestamp) {
      setTimestampError(
        "This timestamp has already been added. Please choose a different timestamp.",
      );
      hasError = true;
    }
    const timestampInSeconds = getTimestampInSeconds(selectedTimestamp);

    if (timestampInSeconds > videoDuration) {
      setTimestampError(
        `Timestamp cannot exceed video duration (${formatTime(videoDuration)})`,
      );
      hasError = true;
    }

    if (!selectedVideo) {
      setVideoError("Please select a video link");
      hasError = true;
    }

    if (hasError) return;
    setTableData((prev) => [
      ...prev,
      {
        id: selectedVideo.id,
        videoId: selectedVideo.id,
        title: selectedVideo.title,
        thumbnail: selectedVideo.thumbnailImage,
        timestamp: selectedTimestamp,
        timestampInSeconds,
        date: new Date().toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "2-digit",
        }),
      },
    ]);

    setSelectedVideo(null);
    setSelectedTimestamp("");
  };

  const deleteRow = (id) => {
    setTableData((prev) => prev.filter((item) => item.id !== id));
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleSave = () => {
    sessionStorage.setItem("previousVideos", JSON.stringify(tableData));

    navigate(-1);
  };
  return (
    <>
      <Header />
      <div className="video-editor-page">
        <div className="editor-header">
          <div className="editor-header-alignments">
            <img
              onClick={() => navigate(-1)}
              src={ArrowLeftIcon}
              alt="Arrow Left"
            />
            <h3>Add Previous Video Link</h3>
          </div>

          <div>
            <button className="discard-btn" onClick={() => navigate(-1)}>
              Discard
            </button>

            <button className="save-btn" onClick={handleSave}>
              Save
            </button>
          </div>
        </div>

        <div className="editor-content">
          <div className="left-panel-video">
            <h4>Editor</h4>

            <label>Video Properties</label>

            <div className="upload-box">
              <div className="upload-alignments">
                <img src={VideoIcon} alt="video-icon" />
                VIDEO
              </div>
            </div>

            <label>Select Timestamp</label>

            {/* <div className="time-input-wrapper">
              <select
                value={selectedTimestamp}
                onChange={(e) => setSelectedTimestamp(e.target.value)}
              >
                <option value="">Select Timestamp</option>

                {generateTimestampOptions().map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
            </div> */}

            <div className="time-input-wrapper">
              <input
                type="text"
                value={selectedTimestamp}
                onChange={(e) => {
                  setSelectedTimestamp(e.target.value);
                  setTimestampError("");
                }}
                placeholder="Examples: 08:30 min, 01:20:15 hour, 45 sec"
                className={timestampError ? "input-error" : ""}
              />
              {timestampError && (
                <p className="field-error-message">{timestampError}</p>
              )}
            </div>
            <label>Video Link</label>
            <p>(Add a video link for reference )</p>

            <input
              readOnly
              value={selectedVideo?.title || ""}
              placeholder="Add your video link"
              onClick={() => {
                setVideoError("");
                setShowModal(true);
              }}
              className={videoError ? "input-error" : ""}
            />
            {videoError && <p className="field-error-message">{videoError}</p>}
            <div className="add-video-btn-wrapper">
              <button className="add-list-btn" onClick={addVideoToList}>
                Add Video To List
              </button>
            </div>
            <h2>Videos with Timestamps</h2>
          </div>

          <div className="video-ref-right-panel">
            {uploadedVideo && (
              <video
                controls
                src={uploadedVideo}
                className="preview-video"
                onLoadedMetadata={(e) =>
                  setVideoDuration(Math.floor(e.target.duration))
                }
              />
            )}
          </div>
        </div>
        <div className="table-section">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Video Title</th>
                <th>Timestamp</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {tableData?.length > 0 ? (
                <>
                  {tableData.map((row) => (
                    <tr key={row.id}>
                      <td>{row.date}</td>

                      <td>
                        <div className="video-cell">
                          <img src={row.thumbnail} alt="" />

                          <h5>{row.title}</h5>
                        </div>
                      </td>

                      <td>{row.timestamp}</td>

                      <td>
                        <button onClick={() => deleteRow(row.id)}>
                          <div className="button-alignments">
                            <img src={DeleteIcon} alt="delete-icon" />
                            Delete
                          </div>
                        </button>
                      </td>
                    </tr>
                  ))}
                </>
              ) : (
                <tr>
                  <td colSpan="4" className="no-data-found">
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {showModal && (
          <VideoSelectionModal
            onClose={() => setShowModal(false)}
            onSelect={(video) => {
              setSelectedVideo(video);
              setShowModal(false);
            }}
          />
        )}
      </div>
    </>
  );
}
